import Model, { attr, hasMany, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { isEmpty, isPresent } from '@ember/utils';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export function occasionPriceOf({
  guestPrice,
  hostEarnings,
}: {
  guestPrice: number | null;
  hostEarnings: number | null;
}): number | null | undefined {
  if (isEmpty(hostEarnings) && (isEmpty(guestPrice) || guestPrice === 0)) {
    return;
  }

  return isPresent(hostEarnings) ? hostEarnings : guestPrice;
}

export default class PriceModel extends Model {
  @attr('number') hostEarnings!: number | null;
  @attr('number') guestPrice!: number | null;
  @attr('number') numberOfGuests!: number | null;
  @attr('string') currency!: string;
  @attr('number') commission!: number | null;
  @attr('number') cancellationPayout!: number | null;
  @attr('number') extraGuest!: number | null;
  @attr('number') extraGuestCharge!: number | null;
  @attr('number') subtotal!: number | null;
  @attr('number') rentTax!: number | null;
  @attr('number') reservationTax!: number;
  @attr('number') personTax!: number | null;
  @attr('number') nightlyTax!: number | null;
  @attr('number') visitorLevy!: number | null;
  @attr('boolean') visitorLevyCollectLocally!: boolean;
  @attr('number') balance!: number;
  @attr('number') grossRevenue!: number;
  @attr('number') managementFee!: number | null;
  @attr('number') averagePricePerNight!: number | null;
  @attr('number') accommodation!: number | null;
  @attr('number') cleaningFee!: number | null;
  @attr('boolean') hostEdited!: boolean | null;
  @attr('number') netRevenue!: number;
  @attr('string') checkIn?: string;
  @attr('string') checkOut?: string;

  @belongsTo('booking', { async: false, inverse: 'price' })
  booking!: ModelRegistry['booking'];

  @hasMany('extra-price', { async: false, inverse: null })
  discounts!: ModelRegistry['extra-price'][];

  @hasMany('extra-price', { async: false, inverse: null })
  charges!: ModelRegistry['extra-price'][];

  @hasMany('extra-price', { async: false, inverse: null })
  fees!: ModelRegistry['extra-price'][];

  @hasMany('extra-price', { async: false, inverse: null })
  taxes!: ModelRegistry['extra-price'][];

  @hasMany('extra-price', { async: false, inverse: null })
  channelFees!: ModelRegistry['extra-price'][];

  @cached
  get visible(): boolean {
    return this.hasHostEarnings || this.hasGuestPrice;
  }

  @cached
  get ownerNetRevenue(): number | undefined {
    if (this.hasNetRevenue && this.hasManagementFee) {
      return (this.netRevenue as number) - (this.managementFee as number);
    }
  }

  @cached
  get hasGuestPrice(): boolean {
    return (this.guestPrice ?? 0) > 0;
  }

  @cached
  get hasCancellationPayout(): boolean {
    return isPresent(this.cancellationPayout);
  }

  @cached
  get hasHostEarnings(): boolean {
    return isPresent(this.hostEarnings);
  }

  @cached
  get hasBalance(): boolean {
    return isPresent(this.balance);
  }

  @cached
  get hasNetRevenue(): boolean {
    return isPresent(this.netRevenue);
  }

  @cached
  get hasManagementFee(): boolean {
    return isPresent(this.managementFee);
  }

  @cached
  get feesWithAmount(): ModelRegistry['extra-price'][] {
    return this.getDataFor('fees');
  }

  @cached
  get taxesWithAmount(): ModelRegistry['extra-price'][] {
    return this.getDataFor('taxes');
  }

  @cached
  get chargesWithAmount(): ModelRegistry['extra-price'][] {
    return this.getDataFor('charges');
  }

  @cached
  get discountsWithAmount(): ModelRegistry['extra-price'][] {
    return this.getDataFor('discounts');
  }

  @cached
  get channelFeesWithAmount(): ModelRegistry['extra-price'][] {
    return this.getDataFor('channelFees');
  }

  @cached
  get occasionPrice(): number | null | undefined {
    return occasionPriceOf(this);
  }

  public dropOrphanedExtraPrices(): void {
    [...this.charges, ...this.discounts]
      .filter((record) => record.isNew)
      .forEach((record) => record.unloadRecord());
  }

  private getDataFor(
    field: 'discounts' | 'charges' | 'fees' | 'taxes' | 'channelFees',
  ): ModelRegistry['extra-price'][] {
    const items = this[field] ?? [];

    return items.filter((item) => isPresent(item.amount));
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    price: PriceModel;
  }
}
